/*
* === UserManagementPage.js (Fixed Inactive Filter Bug) ===
*/
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Container, Typography, Card, Box, Button, Alert, Dialog, DialogActions,
    DialogContent, DialogTitle, TextField, IconButton, Switch, FormControlLabel,
    Grid, Paper, Chip, Snackbar, MenuItem, InputLabel, FormControl, Select
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import KeyIcon from '@mui/icons-material/Key';
import SearchIcon from '@mui/icons-material/Search';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LoginIcon from '@mui/icons-material/Login'; 
import WarningIcon from '@mui/icons-material/Warning';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const RoleChip = ({ role }) => {
    let color = 'default';
    if (role === 'SuperUser') color = 'error';
    if (role === 'Admin') color = 'warning';
    if (role === 'Applicant') color = 'info';
    return <Chip label={role} color={color} size="small" />;
};

const UserManagementPage = () => {
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { token, user: currentUser, login } = useAuth(); 
    
    const location = useLocation();
    const navigate = useNavigate();

    // Filters
    const [filters, setFilters] = useState({ search: '', role: 'All', status: 'All' });
    // REMOVED: const [tabValue, setTabValue] = useState(0); <-- This was causing the bug

    // Modal States
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isImpersonateConfirmOpen, setIsImpersonateConfirmOpen] = useState(false);
    const [isUnlockConfirmOpen, setIsUnlockConfirmOpen] = useState(false);
    
    // Data States
    const [selectedUser, setSelectedUser] = useState(null);
    const [newUserMode, setNewUserMode] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [formError, setFormError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchUsers = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            const response = await api.get('/admin/users', { headers: { Authorization: `Bearer ${token}` } });
            setAllUsers(response.data || []);
        } catch (err) {
            setError('Failed to fetch users.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    // Filtering (FIXED LOGIC)
    const filteredUsers = useMemo(() => {
        return allUsers.filter(user => {
            if (!user) return false;
            const searchLower = filters.search.toLowerCase();
            const fullName = `${user.FirstNames || ''} ${user.Surname || ''}`.toLowerCase();
            const email = (user.Email || '').toLowerCase();
            
            const matchesSearch = !filters.search || fullName.includes(searchLower) || email.includes(searchLower);
            const matchesRole = filters.role === 'All' || user.Role === filters.role;
            const matchesStatus = filters.status === 'All' || 
                                  (filters.status === 'Active' && user.IsActive === 1) ||
                                  (filters.status === 'Inactive' && user.IsActive === 0);

            // REMOVED matchesTab logic here
            return matchesSearch && matchesRole && matchesStatus;
        });
    }, [allUsers, filters]); // REMOVED tabValue from dependency array

    // Handlers
    const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));
    
    const handleCloseModals = () => {
        setIsEditModalOpen(false); setIsPasswordModalOpen(false); 
        setIsDeleteConfirmOpen(false); setIsImpersonateConfirmOpen(false); setIsUnlockConfirmOpen(false);
        setSelectedUser(null);
        if (location.search.includes('action=create')) {
            navigate(location.pathname, { replace: true });
        }
    };
    
    const showToast = (msg, severity = 'success') => setSnackbar({ open: true, message: msg, severity });

    const handleOpenCreateModal = () => {
        setSelectedUser({ firstNames: '', surname: '', Email: '', Role: 'Reviewer', IsActive: true, CompanyId: currentUser.companyId });
        setNewUserMode(true); setIsEditModalOpen(true); setFormError('');
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('action') === 'create' && currentUser) {
            handleOpenCreateModal();
        }
    }, [location.search, currentUser]);

    const handleOpenEditModal = (userToEdit) => {
        setSelectedUser({ ...userToEdit, firstNames: userToEdit.FirstNames, surname: userToEdit.Surname });
        setNewUserMode(false); setIsEditModalOpen(true); setFormError('');
    };
    const handleOpenPasswordModal = (u) => { setSelectedUser(u); setNewPassword(''); setIsPasswordModalOpen(true); };
    const handleOpenDeleteConfirm = (u) => { setSelectedUser(u); setIsDeleteConfirmOpen(true); };
    const handleOpenUnlockConfirm = (u) => { setSelectedUser(u); setIsUnlockConfirmOpen(true); };
    const handleOpenImpersonateConfirm = (u) => { setSelectedUser(u); setIsImpersonateConfirmOpen(true); };

    const handleSaveUser = async () => {
        if (!selectedUser.firstNames || !selectedUser.surname || !selectedUser.Email) {
            setFormError('Names and Email are required.'); return;
        }
        const payload = {
            firstNames: selectedUser.firstNames,
            surname: selectedUser.surname,
            email: selectedUser.Email,
            role: selectedUser.Role,
            companyId: selectedUser.CompanyId || currentUser.companyId,
            isActive: selectedUser.IsActive ? 1 : 0
        };

        try {
            if (newUserMode) await api.post('/admin/users', payload, { headers: { Authorization: `Bearer ${token}` } });
            else await api.put(`/admin/users/${selectedUser.Id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
            fetchUsers(); handleCloseModals();
            showToast(newUserMode ? 'User created.' : 'User updated.');
        } catch (err) { setFormError('Failed to save user.'); }
    };

    const handleImpersonateUser = async () => {
        if (!selectedUser?.Id) return;

        try {
            const { data } = await api.post(
                `/admin/users/${selectedUser.Id}/impersonate`, 
                {}, 
                { headers: { Authorization: `Bearer ${token}` } } 
            );
            
            login(data.token, data.user, true);
            navigate('/');
            
        } catch (error) {
            console.error("Impersonation failed:", error);
            showToast(error.response?.data?.message || 'Impersonation failed.', 'error');
            handleCloseModals();
        }
    };

    const handlePasswordReset = async () => {
        if (newPassword.length < 6) { setFormError('Password must be at least 6 characters.'); return; }
        try {
            await api.put(`/admin/users/${selectedUser.Id}/password`, { newPassword }, { headers: { Authorization: `Bearer ${token}` } });
            handleCloseModals(); showToast('Password updated successfully.');
        } catch (err) { setFormError('Failed to update password.'); }
    };

    const handleDeleteUser = async () => {
        try { 
            await api.delete(`/admin/users/${selectedUser.Id}`, { headers: { Authorization: `Bearer ${token}` } }); 
            fetchUsers(); handleCloseModals(); showToast('User deleted successfully.', 'success');
        } catch (e) { handleCloseModals(); showToast(e.response?.data?.message || 'Failed to delete user.', 'error'); }
    };

    const handleUnlockUser = async () => {
        try { await api.put(`/admin/users/${selectedUser.Id}/unlock`, {}, { headers: { Authorization: `Bearer ${token}` } }); fetchUsers(); handleCloseModals(); showToast('Account unlocked.', 'success'); } catch (e) { showToast('Failed to unlock account.', 'error'); }
    };

    // Note: Render logic for the DataGrid table columns below has been fixed by the new filtering mechanism.
    const columns = [
        { field: 'Id', headerName: 'ID', width: 70 },
        { 
            field: 'fullName', headerName: 'Full Name', flex: 1, minWidth: 150,
            valueGetter: (params) => `${params.row.FirstNames || ''} ${params.row.Surname || ''}`.trim()
        },
        { field: 'Email', headerName: 'Email', flex: 1, minWidth: 200 },
        { field: 'Role', headerName: 'Role', width: 130, renderCell: (params) => <RoleChip role={params.value} /> },
        { field: 'IsActive', headerName: 'Status', width: 100, renderCell: (p) => <Chip label={p.value ? "Active" : "Inactive"} color={p.value ? "success" : "default"} size="small" variant="outlined" /> },
        {
            field: 'actions', headerName: 'Actions', width: 250, sortable: false,
            renderCell: (params) => (
                <Box>
                    {currentUser.role === 'SuperUser' && params.row.Role !== 'SuperUser' && (
                         <IconButton color="secondary" size="small" title="Log In As" onClick={() => handleOpenImpersonateConfirm(params.row)}><LoginIcon /></IconButton>
                    )}
                    <IconButton color="primary" size="small" onClick={() => handleOpenEditModal(params.row)}><EditIcon /></IconButton>
                    <IconButton color="default" size="small" onClick={() => handleOpenPasswordModal(params.row)}><KeyIcon /></IconButton>
                    <IconButton color="error" size="small" onClick={() => handleOpenDeleteConfirm(params.row)}><DeleteIcon /></IconButton>
                    {params.row.IsActive === 0 && <IconButton color="success" size="small" onClick={() => handleOpenUnlockConfirm(params.row)}><LockOpenIcon /></IconButton>}
                </Box>
            )
        }
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" fontWeight="bold">User Management</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateModal}>Add User</Button>
            </Box>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" label="Search Name or Email" value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)} InputProps={{ endAdornment: <SearchIcon /> }} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Role</InputLabel>
                            <Select value={filters.role} label="Role" onChange={(e) => handleFilterChange('role', e.target.value)}>
                                <MenuItem value="All">All Roles</MenuItem>
                                <MenuItem value="Admin">Admin</MenuItem>
                                <MenuItem value="Reviewer">Reviewer</MenuItem>
                                <MenuItem value="Applicant">Applicant</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select value={filters.status} label="Status" onChange={(e) => handleFilterChange('status', e.target.value)}>
                                <MenuItem value="All">All Statuses</MenuItem>
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="Inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}><Button variant="outlined" onClick={() => setFilters({ search: '', role: 'All', status: 'All' })} fullWidth>Reset</Button></Grid>
                </Grid>
            </Paper>
            
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Card>
                <Box sx={{ height: 600, width: '100%' }}>
                    <DataGrid
                        rows={filteredUsers}
                        columns={columns}
                        getRowId={(row) => row.Id}
                        loading={loading}
                        disableRowSelectionOnClick
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                        pageSizeOptions={[10, 25, 50]}
                    />
                </Box>
            </Card>

            <Dialog open={isEditModalOpen} onClose={handleCloseModals}>
                <DialogTitle>{newUserMode ? 'Add User' : 'Edit User'}</DialogTitle>
                <DialogContent>
                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}><TextField label="First Name" value={selectedUser?.firstNames || ''} onChange={(e) => setSelectedUser({...selectedUser, firstNames: e.target.value})} fullWidth /></Grid>
                        <Grid item xs={6}><TextField label="Surname" value={selectedUser?.surname || ''} onChange={(e) => setSelectedUser({...selectedUser, surname: e.target.value})} fullWidth /></Grid>
                    </Grid>
                    <TextField margin="dense" label="Email" fullWidth value={selectedUser?.Email || ''} onChange={(e) => setSelectedUser({...selectedUser, Email: e.target.value})} />
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Role</InputLabel>
                        <Select value={selectedUser?.Role || 'Reviewer'} label="Role" onChange={(e) => setSelectedUser({...selectedUser, Role: e.target.value})}>
                            {currentUser.role === 'SuperUser' && <MenuItem value="SuperUser">SuperUser</MenuItem>}
                            <MenuItem value="Admin">Admin</MenuItem>
                            <MenuItem value="Reviewer">Reviewer</MenuItem>
                            <MenuItem value="Finance">Finance</MenuItem>
                            <MenuItem value="Applicant">Applicant</MenuItem>
                        </Select>
                    </FormControl>
                    {!newUserMode && <FormControlLabel control={<Switch checked={selectedUser?.IsActive === 1} onChange={(e) => setSelectedUser({...selectedUser, IsActive: e.target.checked ? 1 : 0})} />} label="Active" sx={{ mt: 2 }} />}
                </DialogContent>
                <DialogActions><Button onClick={handleCloseModals}>Cancel</Button><Button onClick={handleSaveUser} variant="contained">Save</Button></DialogActions>
            </Dialog>

             <Dialog open={isImpersonateConfirmOpen} onClose={handleCloseModals}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}><WarningIcon sx={{ mr: 1 }} /> Security Warning</DialogTitle>
                <DialogContent>
                    <Typography paragraph>You are about to impersonate <strong>{selectedUser?.FirstNames} {selectedUser?.Surname}</strong> ({selectedUser?.Email}).</Typography>
                    <Alert severity="warning" sx={{ mb: 2 }}><strong>This action will be logged.</strong><br/>Any actions you perform will be recorded as if they were performed by this user.</Alert>
                </DialogContent>
                <DialogActions><Button onClick={handleCloseModals}>Cancel</Button><Button onClick={handleImpersonateUser} variant="contained" color="warning">I Understand, Login As User</Button></DialogActions>
            </Dialog>
            
             <Dialog open={isPasswordModalOpen} onClose={handleCloseModals}>
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                    <TextField autoFocus margin="dense" label="New Password" type="password" fullWidth value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                </DialogContent>
                <DialogActions><Button onClick={handleCloseModals}>Cancel</Button><Button onClick={handlePasswordReset} variant="contained">Set Password</Button></DialogActions>
            </Dialog>
            
             <Dialog open={isDeleteConfirmOpen} onClose={handleCloseModals}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent><Typography>Are you sure you want to delete user <strong>{selectedUser?.FirstNames}</strong>?</Typography></DialogContent>
                <DialogActions><Button onClick={handleCloseModals}>Cancel</Button><Button onClick={handleDeleteUser} color="error" variant="contained">Delete</Button></DialogActions>
            </Dialog>

             <Dialog open={isUnlockConfirmOpen} onClose={handleCloseModals}>
                <DialogTitle>Unlock Account</DialogTitle>
                <DialogContent><Typography>Unlock account for <strong>{selectedUser?.FirstNames}</strong>?</Typography></DialogContent>
                <DialogActions><Button onClick={handleCloseModals}>Cancel</Button><Button onClick={handleUnlockUser} color="success" variant="contained">Unlock</Button></DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

export default UserManagementPage;
